import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const OP_LABELS = { gt: '>', lt: '<', gte: '≥', lte: '≤', eq: '=', neq: '≠' };

export function AlertFromVizDialog({ open, onOpenChange, alertData, setAlertData }) {
  const availableMetrics = (alertData._availableMetrics && alertData._availableMetrics.length)
    ? alertData._availableMetrics
    : (alertData.metric ? [{ value: alertData.metric, label: alertData._metricLabel || alertData.metric }] : []);

  // Rules default to the single seeded condition; user can add more.
  const rules = alertData.rules || [{ metric: alertData.metric, operator: alertData.operator || 'gt', threshold: alertData.threshold ?? '' }];
  const setRules = (next) => setAlertData(p => ({ ...p, rules: next }));
  const updateRule = (i, patch) => setRules(rules.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRule = () => setRules([...rules, { metric: alertData.metric, operator: 'gt', threshold: '' }]);
  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));
  const logic = alertData.logic || 'AND';
  const multi = rules.length > 1;

  const labelFor = (m) => (availableMetrics.find(x => x.value === m) || {}).label || (m || '').replace(/_/g, ' ');

  const create = async () => {
    const cleanRules = rules
      .filter(r => r.metric && r.threshold !== '' && r.threshold !== null && r.threshold !== undefined)
      .map(r => ({ metric: r.metric, operator: r.operator, threshold: Number(r.threshold) }));
    if (!cleanRules.length) { toast.error('Add at least one complete condition'); return; }
    const first = cleanRules[0];
    try {
      await api.createAlert({
        name: alertData.name,
        // Legacy single-condition fields keep older evaluators/lists happy.
        metric: first.metric,
        operator: first.operator,
        threshold: first.threshold,
        // Multi-condition tree (evaluator prefers this when present).
        conditions: { logic, rules: cleanRules },
        check_frequency: alertData.frequency,
        recipients: alertData.recipients.split(',').map(s => s.trim()).filter(Boolean),
        severity: 'medium',
      });
      toast.success(multi ? `Alert created (${cleanRules.length} conditions, ${logic})` : 'Alert created from visual');
      onOpenChange(false);
    } catch (err) { toast.error(err.message); }
  };

  const canCreate = rules.some(r => r.metric && r.threshold !== '' && r.threshold !== null && r.threshold !== undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="alert-from-viz-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Bell className="w-4 h-4 text-amber-400" /> Create Alert from Visual</DialogTitle><DialogDescription className="text-xs text-muted-foreground">Fire when your conditions are met.</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label className="text-xs text-muted-foreground">Alert Name</Label><Input value={alertData.name} onChange={e => setAlertData(p => ({ ...p, name: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 mt-1 text-foreground" /></div>
          <div className="p-2.5 rounded-lg bg-muted/20 border border-border/30 text-[10px]">
            <p className="text-muted-foreground">Visual: <span className="text-foreground">{alertData._vizTitle}</span></p>
            {alertData._currentValue !== undefined && <p className="text-muted-foreground">Current value: <span className="text-foreground font-mono">{typeof alertData._currentValue === 'number' ? alertData._currentValue.toLocaleString() : alertData._currentValue}</span></p>}
          </div>

          {/* Conditions */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Conditions</Label>
              {multi && (
                <div className="flex items-center gap-1 text-[10px]" data-testid="alert-logic-toggle">
                  <span className="text-muted-foreground">Match</span>
                  <button type="button" onClick={() => setAlertData(p => ({ ...p, logic: 'AND' }))}
                    className={`px-2 py-0.5 rounded-md border ${logic === 'AND' ? 'bg-amber-500/20 border-amber-400/50 text-amber-300' : 'border-border/50 text-muted-foreground'}`} data-testid="alert-logic-and">ALL (AND)</button>
                  <button type="button" onClick={() => setAlertData(p => ({ ...p, logic: 'OR' }))}
                    className={`px-2 py-0.5 rounded-md border ${logic === 'OR' ? 'bg-amber-500/20 border-amber-400/50 text-amber-300' : 'border-border/50 text-muted-foreground'}`} data-testid="alert-logic-or">ANY (OR)</button>
                </div>
              )}
            </div>

            {rules.map((r, i) => (
              <div key={i} className="space-y-1" data-testid={`alert-rule-${i}`}>
                {i > 0 && <div className="text-[9px] uppercase tracking-wider text-amber-400/70 pl-1">{logic}</div>}
                <div className="flex items-start gap-1.5">
                  <div className="flex-1 grid grid-cols-3 gap-1.5">
                    <Select value={r.metric} onValueChange={v => updateRule(i, { metric: v })}>
                      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs col-span-3 sm:col-span-1" data-testid={`alert-rule-metric-${i}`}><SelectValue placeholder="Metric" /></SelectTrigger>
                      <SelectContent>{availableMetrics.map(m => <SelectItem key={m.value} value={m.value} className="text-xs">{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={r.operator} onValueChange={v => updateRule(i, { operator: v })}>
                      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs" data-testid={`alert-rule-op-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gt">Greater Than</SelectItem><SelectItem value="lt">Less Than</SelectItem>
                        <SelectItem value="gte">Greater or Equal</SelectItem><SelectItem value="lte">Less or Equal</SelectItem>
                        <SelectItem value="eq">Equals</SelectItem><SelectItem value="neq">Not Equals</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" value={r.threshold} onChange={e => updateRule(i, { threshold: e.target.value })} placeholder="Value" className="rounded-lg bg-muted/50 border-border/50 text-foreground text-xs" data-testid={`alert-rule-threshold-${i}`} />
                  </div>
                  {rules.length > 1 && (
                    <button type="button" onClick={() => removeRule(i)} className="mt-1.5 text-muted-foreground hover:text-destructive" data-testid={`alert-rule-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
            ))}
            <button type="button" onClick={addRule} className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="alert-add-condition"><Plus className="w-3 h-3" /> Add condition</button>
          </div>

          {multi && (
            <p className="text-[10px] text-muted-foreground bg-muted/20 rounded-md px-2 py-1.5" data-testid="alert-conditions-preview">
              Fires when <strong>{logic === 'AND' ? 'all' : 'any'}</strong>: {rules.filter(r => r.metric && r.threshold !== '').map(r => `${labelFor(r.metric)} ${OP_LABELS[r.operator] || r.operator} ${r.threshold}`).join(logic === 'AND' ? ' and ' : ' or ')}
            </p>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-muted-foreground">Check Frequency</Label>
              <Select value={alertData.frequency} onValueChange={v => setAlertData(p => ({ ...p, frequency: v }))}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-muted-foreground">Severity</Label>
              <Select value="medium" onValueChange={() => {}}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs text-muted-foreground">Recipients</Label><Input value={alertData.recipients} onChange={e => setAlertData(p => ({ ...p, recipients: e.target.value }))} placeholder="email@company.com" className="rounded-lg bg-muted/50 border-border/50 mt-1 text-foreground" /></div>
          <Button onClick={create} disabled={!canCreate} className="w-full rounded-lg" data-testid="confirm-alert-from-viz">
            <Bell className="w-4 h-4 mr-1" /> Create Alert
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
